'use client';

import React, { useState, useEffect } from 'react';
import PlatformNav from '@/components/PlatformNav';
import Icon from '@/components/ui/AppIcon';

interface InsuranceProduct {
  id: string;
  product_code: string;
  name: string;
  insurance_type: string;
  description: string;
  coverage_summary: Record<string, string>;
  base_price: number;
  price_per_day: number;
  currency: string;
  max_trip_days: number;
  max_age: number;
  is_featured: boolean;
}

const INSURANCE_TYPES = [
  { value: '', label: 'All Plans', icon: 'ShieldCheckIcon' },
  { value: 'travel', label: 'Travel', icon: 'GlobeAltIcon' },
  { value: 'medical', label: 'Medical', icon: 'HeartIcon' },
  { value: 'annual_multi_trip', label: 'Annual', icon: 'CalendarDaysIcon' },
  { value: 'cruise', label: 'Cruise', icon: 'MapIcon' },
  { value: 'adventure', label: 'Adventure', icon: 'BoltIcon' },
];

const TYPE_COLORS: Record<string, string> = {
  travel: '#3b82f6',
  medical: '#ef4444',
  annual_multi_trip: '#8b5cf6',
  cruise: '#06b6d4',
  adventure: '#f59e0b',
  trip_cancellation: '#10b981',
  baggage: '#6366f1',
  business_travel: '#64748b',
  group: '#ec4899',
};

export default function InsurancePage() {
  const [products, setProducts] = useState<InsuranceProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<InsuranceProduct | null>(null);
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [quoteForm, setQuoteForm] = useState({
    contact_name: '', contact_email: '', contact_phone: '',
    travel_date: '', destination: '', travelers_count: '1',
    budget_amount: '', requirements: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, [selectedType]);

  async function fetchProducts() {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '20' });
      if (selectedType) params.set('insurance_type', selectedType);
      const res = await fetch(`/api/insurance-products?${params}`);
      const json = await res.json();
      setProducts(json.data || []);
    } catch {
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }

  async function handleQuoteSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await fetch('/api/service-leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...quoteForm,
          service_type: 'insurance',
          service_id: selectedProduct?.id,
          source: 'insurance_page',
        }),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-background)' }}>
      <PlatformNav />
      <div className="pt-16">
        {/* Hero */}
        <section className="relative py-20 px-6 overflow-hidden">
          <div className="absolute inset-0 opacity-10"
            style={{ background: 'radial-gradient(ellipse at 70% 50%, var(--color-primary) 0%, transparent 60%)' }} />
          <div className="max-w-5xl mx-auto relative">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: 'var(--color-primary-10)' }}>
                <Icon name="ShieldCheckIcon" size={20} className="text-primary" />
              </div>
              <span className="label-caps text-primary">Travel Insurance</span>
            </div>
            <h1 className="font-serif text-4xl md:text-5xl font-semibold text-foreground mb-4">
              Travel with Complete Confidence
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mb-8">
              Comprehensive travel insurance designed for luxury travellers. From medical emergencies to trip cancellations — we ensure your journey is protected at every step.
            </p>
            <div className="flex flex-wrap gap-6 text-sm text-muted-foreground">
              {[
                { icon: 'BoltIcon', label: 'Instant policy issuance' },
                { icon: 'PhoneIcon', label: '24/7 emergency assistance' },
                { icon: 'CurrencyDollarIcon', label: 'Up to $1M medical coverage' },
                { icon: 'GlobeAltIcon', label: 'Worldwide coverage' },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2">
                  <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={16} className="text-primary" />
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Type Filter */}
        <section className="px-6 pb-8">
          <div className="max-w-5xl mx-auto">
            <div className="flex gap-2 flex-wrap">
              {INSURANCE_TYPES.map((type) => (
                <button
                  key={type.value}
                  onClick={() => setSelectedType(type.value)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                    selectedType === type.value
                      ? 'bg-primary text-white' :'bg-surface-2 text-muted-foreground hover:text-foreground border border-border'
                  }`}
                >
                  <Icon name={type.icon as Parameters<typeof Icon>[0]['name']} size={14} />
                  {type.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Products */}
        <section className="px-6 pb-16">
          <div className="max-w-5xl mx-auto">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="h-64 rounded-xl bg-surface-2 animate-pulse" />
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-16">
                <Icon name="ShieldCheckIcon" size={40} className="text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No insurance products found.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {products.map((product) => {
                  const color = TYPE_COLORS[product.insurance_type] || 'var(--color-primary)';
                  return (
                    <div
                      key={product.id}
                      className="rounded-xl border border-border bg-surface-2 overflow-hidden hover:border-primary/40 transition-all cursor-pointer group"
                      onClick={() => setSelectedProduct(product)}
                    >
                      <div className="h-1.5" style={{ background: color }} />
                      <div className="p-5">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors text-sm leading-tight">
                              {product.name}
                            </h3>
                            <span className="text-xs text-muted-foreground capitalize">
                              {product.insurance_type?.replace(/_/g, ' ')}
                            </span>
                          </div>
                          {product.is_featured && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary shrink-0">
                              Popular
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mb-4 line-clamp-2">{product.description}</p>

                        {/* Coverage highlights */}
                        <div className="space-y-1.5 mb-4">
                          {Object.entries(product.coverage_summary || {}).slice(0, 3).map(([key, value]) => (
                            <div key={key} className="flex items-center justify-between text-xs">
                              <span className="text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</span>
                              <span className="font-medium text-foreground">{value}</span>
                            </div>
                          ))}
                        </div>

                        <div className="flex items-center justify-between pt-3 border-t border-border">
                          <div>
                            <p className="text-xs text-muted-foreground">From</p>
                            <p className="font-semibold text-foreground">
                              {product.currency} {product.base_price || product.price_per_day}
                              <span className="text-xs text-muted-foreground font-normal">
                                {product.base_price ? '/trip' : '/day'}
                              </span>
                            </p>
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); setSelectedProduct(product); setShowQuoteForm(true); }}
                            className="px-3 py-1.5 rounded-lg text-xs font-medium text-white transition-all"
                            style={{ background: color }}
                          >
                            Get Quote
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {/* Why Choose Section */}
        <section className="px-6 pb-16">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="rounded-2xl border border-border bg-surface-2 p-6">
                <h2 className="font-serif text-xl font-semibold text-foreground mb-4">Why WorldwayLuxe Insurance?</h2>
                <div className="space-y-3">
                  {[
                    { icon: 'BoltIcon', title: 'Instant Coverage', desc: 'Policy issued within minutes of purchase' },
                    { icon: 'PhoneIcon', title: '24/7 Emergency Line', desc: 'Dedicated luxury travel assistance hotline' },
                    { icon: 'CurrencyDollarIcon', title: 'Fast Claims', desc: 'Most claims processed within 5 business days' },
                    { icon: 'StarIcon', title: 'Concierge Claims', desc: 'Personal claims manager for premium policies' },
                  ].map((item) => (
                    <div key={item.title} className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                        style={{ background: 'var(--color-primary-10)' }}>
                        <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={14} className="text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">{item.title}</p>
                        <p className="text-xs text-muted-foreground">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-2xl border border-primary/20 p-6 flex flex-col justify-between"
                style={{ background: 'linear-gradient(135deg, var(--color-primary-10) 0%, transparent 100%)' }}>
                <div>
                  <h2 className="font-serif text-xl font-semibold text-foreground mb-3">Need a custom plan?</h2>
                  <p className="text-sm text-muted-foreground mb-6">
                    Group travel, corporate policies, or extended coverage? Our insurance specialists will design a bespoke plan for your needs.
                  </p>
                </div>
                <button
                  onClick={() => { setSelectedProduct(null); setShowQuoteForm(true); }}
                  className="w-full py-3 rounded-xl font-medium text-white transition-all"
                  style={{ background: 'var(--color-primary)' }}
                >
                  Request Custom Quote
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Product Detail Modal */}
      {selectedProduct && !showQuoteForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setSelectedProduct(null)}>
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <div
            className="relative w-full max-w-lg rounded-2xl border border-border bg-surface-2 p-6 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={() => setSelectedProduct(null)} className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-surface-3 text-muted-foreground">
              <Icon name="XMarkIcon" size={18} />
            </button>
            <div className="h-1 rounded-full mb-4" style={{ background: TYPE_COLORS[selectedProduct.insurance_type] || 'var(--color-primary)' }} />
            <h2 className="font-serif text-xl font-semibold text-foreground mb-1">{selectedProduct.name}</h2>
            <p className="text-sm text-muted-foreground mb-4">{selectedProduct.description}</p>
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-foreground mb-2">Coverage Summary</h3>
              <div className="space-y-2">
                {Object.entries(selectedProduct.coverage_summary || {}).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between p-2 rounded-lg bg-surface-3">
                    <span className="text-sm text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</span>
                    <span className="text-sm font-medium text-foreground">{value}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between p-3 rounded-xl bg-surface-3 mb-4">
              <div>
                <p className="text-xs text-muted-foreground">Starting from</p>
                <p className="text-lg font-semibold text-foreground">
                  {selectedProduct.currency} {selectedProduct.base_price || selectedProduct.price_per_day}
                  <span className="text-sm text-muted-foreground font-normal">
                    {selectedProduct.base_price ? ' / trip' : ' / day'}
                  </span>
                </p>
              </div>
              {selectedProduct.max_trip_days && (
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">Max trip</p>
                  <p className="text-sm font-medium text-foreground">{selectedProduct.max_trip_days} days</p>
                </div>
              )}
            </div>
            <button
              onClick={() => setShowQuoteForm(true)}
              className="w-full py-3 rounded-xl font-medium text-white transition-all"
              style={{ background: TYPE_COLORS[selectedProduct.insurance_type] || 'var(--color-primary)' }}
            >
              Get a Quote
            </button>
          </div>
        </div>
      )}

      {/* Quote Form Modal */}
      {showQuoteForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => { setShowQuoteForm(false); setSubmitted(false); }}>
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <div
            className="relative w-full max-w-md rounded-2xl border border-border bg-surface-2 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={() => { setShowQuoteForm(false); setSubmitted(false); }} className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-surface-3 text-muted-foreground">
              <Icon name="XMarkIcon" size={18} />
            </button>
            {submitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'var(--color-primary-10)' }}>
                  <Icon name="CheckCircleIcon" size={32} className="text-primary" />
                </div>
                <h3 className="font-serif text-xl font-semibold text-foreground mb-2">Quote Request Received</h3>
                <p className="text-muted-foreground text-sm">Our insurance specialist will send your personalised quote within 1 business hour.</p>
              </div>
            ) : (
              <>
                <h2 className="font-serif text-xl font-semibold text-foreground mb-1">
                  {selectedProduct ? `Quote: ${selectedProduct.name}` : 'Insurance Quote'}
                </h2>
                <p className="text-sm text-muted-foreground mb-5">Get a personalised insurance quote for your trip.</p>
                <form onSubmit={handleQuoteSubmit} className="space-y-3">
                  <input type="text" placeholder="Full Name *" required
                    value={quoteForm.contact_name}
                    onChange={(e) => setQuoteForm({ ...quoteForm, contact_name: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-lg text-sm border border-border bg-surface-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
                  />
                  <input type="email" placeholder="Email Address *" required
                    value={quoteForm.contact_email}
                    onChange={(e) => setQuoteForm({ ...quoteForm, contact_email: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-lg text-sm border border-border bg-surface-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
                  />
                  <input type="tel" placeholder="Phone Number"
                    value={quoteForm.contact_phone}
                    onChange={(e) => setQuoteForm({ ...quoteForm, contact_phone: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-lg text-sm border border-border bg-surface-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
                  />
                  <input type="text" placeholder="Destination"
                    value={quoteForm.destination}
                    onChange={(e) => setQuoteForm({ ...quoteForm, destination: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-lg text-sm border border-border bg-surface-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <input type="date" placeholder="Travel Date"
                      value={quoteForm.travel_date}
                      onChange={(e) => setQuoteForm({ ...quoteForm, travel_date: e.target.value })}
                      className="w-full px-3 py-2.5 rounded-lg text-sm border border-border bg-surface-3 text-foreground focus:outline-none focus:border-primary"
                    />
                    <input type="number" placeholder="Travelers" min="1"
                      value={quoteForm.travelers_count}
                      onChange={(e) => setQuoteForm({ ...quoteForm, travelers_count: e.target.value })}
                      className="w-full px-3 py-2.5 rounded-lg text-sm border border-border bg-surface-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
                    />
                  </div>
                  <textarea placeholder="Additional requirements..."
                    rows={2}
                    value={quoteForm.requirements}
                    onChange={(e) => setQuoteForm({ ...quoteForm, requirements: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-lg text-sm border border-border bg-surface-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary resize-none"
                  />
                  <button type="submit" disabled={submitting}
                    className="w-full py-3 rounded-xl font-medium text-white transition-all disabled:opacity-50"
                    style={{ background: 'var(--color-primary)' }}
                  >
                    {submitting ? 'Submitting...' : 'Request Quote'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
